import React, { useState, useEffect, useRef } from "react";
import "./cityPicker.css";

const IconCheck = () => (
  <svg className="sr-city-picker__check" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
  </svg>
);

const IconSearch = () => (
  <svg className="sr-city-picker__search-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5Zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14Z" />
  </svg>
);

const HighlightMatch = ({ text, query }) => {
  if (!query || !text.toLowerCase().startsWith(query.toLowerCase())) return <span>{text}</span>;
  return (
    <span>
      <mark className="sr-city-picker__match">{text.slice(0, query.length)}</mark>
      {text.slice(query.length)}
    </span>
  );
};

const CityPicker = ({ cities, value, onChange, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 550);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);

  const filtered = query
    ? cities.filter((c) => c.toLowerCase().startsWith(query.toLowerCase()))
    : cities;

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 550);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen]);

  // Clear query when closed
  useEffect(() => {
    if (!isOpen) setQuery("");
  }, [isOpen]);

  // Close desktop dropdown on outside click
  useEffect(() => {
    if (!isOpen || isMobile) return;

    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [isOpen, isMobile]);

  const handleSelect = (city) => {
    onChange(city);
    setIsOpen(false);
  };

  const renderSearch = () => (
    <div className="sr-city-picker__search">
      <IconSearch />
      <input
        type="text"
        className="sr-city-picker__search-input"
        placeholder="Search city..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
    </div>
  );

  const renderList = () => (
    <ul className="sr-city-picker__list">
      {filtered.length === 0 && (
        <li className="sr-city-picker__empty">No cities found</li>
      )}
      {filtered.map((city) => (
        <li
          key={city}
          className={`sr-city-picker__item ${city === value ? "sr-city-picker__item--selected" : ""}`}
          onClick={() => handleSelect(city)}
        >
          <HighlightMatch text={city} query={query} />
          {city === value && <IconCheck />}
        </li>
      ))}
    </ul>
  );

  return (
    <div className="sr-city-picker" ref={containerRef}>
      <button
        type="button"
        className="sr-city-picker__trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        {value || (label ? `Select ${label}` : "Select city")}
      </button>

      {/* Desktop dropdown */}
      {isOpen && !isMobile && (
        <div className="sr-city-picker__dropdown">
          {label && <div className="sr-city-picker__header">{label}</div>}
          {renderSearch()}
          {renderList()}
        </div>
      )}

      {/* Mobile bottom sheet */}
      {isOpen && isMobile && (
        <div className="sr-city-picker__overlay" onClick={() => setIsOpen(false)}>
          <div
            className="sr-city-picker__sheet"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sr-city-picker__sheet-handle" />
            {label && <div className="sr-city-picker__header">{label}</div>}
            {renderSearch()}
            {renderList()}
          </div>
        </div>
      )}
    </div>
  );
};

export default CityPicker;
